/**
 * Phase 297 — Parent NPS / Google Review Receiver
 *
 * Lives at `/feedback/:token` — fully PUBLIC (no login required). The signed
 * JWT in the URL is the sole credential, scoped to one (session, student,
 * parent) triple, TTL'd to 7 days.
 *
 * Flow:
 *   1. GET /api/feedback/:token → { student_name, academy_name, has_google_review_url, already_submitted }.
 *   2. Parent taps any of 5 stars.
 *   3. POST /api/feedback/:token { rating, comments? }.
 *   4. 4-5★ + URL configured → window.open(redirect_url) + show success card.
 *      4-5★ + no URL          → show in-app "Thank you" card.
 *      1-3★                   → expand text area → submit → show "Thanks, we'll follow up" card.
 *
 * Design intent: lightweight, mobile-first, outdoor-sun friendly white bg
 * with one dominant action color (amber for stars, emerald for success,
 * rose for low-rating empathy). Zero spinner waterfall — feels instant.
 */
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Star, CheckCircle2, AlertTriangle, Loader2, Heart, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

// Phase 297 uses a *public* axios instance (no global 401 interceptor, no
// Authorization header) so the parent never sees an auth flow.
const PUBLIC_API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const publicApi = axios.create({ baseURL: PUBLIC_API_BASE, timeout: 15000 });

function StarRow({ rating, hover, onHover, onLeave, onTap, disabled }) {
  return (
    <div className="flex items-center justify-center gap-1 select-none" data-testid="feedback-star-row">
      {[1, 2, 3, 4, 5].map((n) => {
        const active = (hover || rating) >= n;
        return (
          <button
            key={n}
            type="button"
            aria-label={`${n} star${n === 1 ? '' : 's'}`}
            disabled={disabled}
            onMouseEnter={() => onHover(n)}
            onMouseLeave={() => onLeave()}
            onClick={() => onTap(n)}
            className={`p-1.5 sm:p-2 transition-transform duration-150 ${active ? 'scale-110' : 'scale-100'} ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:scale-110 cursor-pointer'}`}
            data-testid={`feedback-star-${n}`}
          >
            <Star
              className={`h-10 w-10 sm:h-12 sm:w-12 transition-colors ${active ? 'fill-amber-400 text-amber-500 drop-shadow-[0_0_8px_rgba(251,191,36,0.6)]' : 'text-slate-300'}`}
              strokeWidth={2}
            />
          </button>
        );
      })}
    </div>
  );
}

export default function FeedbackRouter() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null); // null = loading
  const [tokenError, setTokenError] = useState(null);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { rating, redirected, message }
  const [showLowRatingForm, setShowLowRatingForm] = useState(false);

  // ── Hydrate meta on mount ────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    publicApi.get(`/feedback/${token}`)
      .then((r) => { if (!cancelled) setMeta(r.data); })
      .catch((err) => {
        if (cancelled) return;
        const detail = err?.response?.data?.detail || 'This feedback link is no longer valid.';
        setTokenError(detail);
      });
    return () => { cancelled = true; };
  }, [token]);

  // ── Submit helpers ───────────────────────────────────────────────────
  const doSubmit = useCallback(async (finalRating, finalComments) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const r = await publicApi.post(`/feedback/${token}`, { rating: finalRating, comments: finalComments });
      setResult(r.data);
      if (r.data?.redirect_url) {
        // Brief delay so the parent reads the "Redirecting you to Google" message.
        setTimeout(() => { window.open(r.data.redirect_url, '_blank', 'noopener'); }, 900);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Something went wrong — please try again.';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  }, [submitting, token]);

  const handleStarTap = useCallback((n) => {
    setRating(n);
    if (n >= 4) {
      // High rating → fire instantly. We trust the tap; no extra confirmation.
      doSubmit(n, '');
    } else {
      // Low rating → expand the comment area; parent submits explicitly.
      setShowLowRatingForm(true);
    }
  }, [doSubmit]);

  const handleLowRatingSubmit = useCallback((e) => {
    e?.preventDefault?.();
    if (rating < 1 || rating > 3) return;
    doSubmit(rating, comments);
  }, [rating, comments, doSubmit]);

  // ────────────────────────────────────────────────────────────────────
  // RENDER
  // ────────────────────────────────────────────────────────────────────
  if (tokenError) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6 text-center" data-testid="feedback-token-error-state">
        <AlertTriangle className="h-14 w-14 text-rose-500 mb-4" />
        <h1 className="text-xl font-bold text-slate-900 mb-1">Link no longer valid</h1>
        <p className="text-sm text-slate-600 max-w-sm">{tokenError}</p>
        <p className="text-xs text-slate-400 mt-6">Out-Play.in · Parent Feedback</p>
      </div>
    );
  }

  if (!meta) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center text-slate-500">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  // Already-submitted short-circuit
  if (meta.already_submitted && !result) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6 text-center" data-testid="feedback-already-submitted-state">
        <Heart className="h-14 w-14 text-rose-400 mb-4" />
        <h1 className="text-xl font-bold text-slate-900 mb-1">Thanks for your feedback!</h1>
        <p className="text-sm text-slate-600 max-w-sm">
          You already shared {meta.previous_rating || 'your'} stars for this session — we love that.
        </p>
        <p className="text-xs text-slate-400 mt-8">Out-Play.in · Parent Feedback</p>
      </div>
    );
  }

  // Success state (post-submit)
  if (result) {
    const isHigh = result.rating >= 4;
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6 text-center" data-testid="feedback-success-state">
        <div className={`mx-auto w-20 h-20 rounded-full flex items-center justify-center mb-4 ${isHigh ? 'bg-emerald-100' : 'bg-rose-100'}`}>
          {isHigh
            ? <CheckCircle2 className="h-12 w-12 text-emerald-600" strokeWidth={2.5} />
            : <MessageCircle className="h-12 w-12 text-rose-500" strokeWidth={2} />}
        </div>
        <h1 className={`text-2xl font-display font-bold mb-2 ${isHigh ? 'text-emerald-700' : 'text-rose-700'}`} data-testid="feedback-success-headline">
          {isHigh ? 'Thank you!' : 'Got it — thank you'}
        </h1>
        <p className="text-sm text-slate-600 max-w-sm mb-2" data-testid="feedback-success-message">
          {result.message}
        </p>
        {result.redirect_url && (
          <a
            href={result.redirect_url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 inline-block text-sm font-semibold text-emerald-700 underline underline-offset-4"
            data-testid="feedback-google-redirect-link"
          >
            Open Google Reviews
          </a>
        )}
        <p className="text-xs text-slate-400 mt-10">Out-Play.in · Parent Feedback</p>
      </div>
    );
  }

  // ── Primary CAPTURE state (stars + optional low-rating form) ─────────
  return (
    <div className="min-h-screen bg-white text-slate-900 flex flex-col" data-testid="feedback-capture-state">
      <header className="px-5 py-3 border-b border-slate-200 flex items-center justify-center">
        <p className="text-sm font-semibold text-emerald-700 tracking-wide" data-testid="feedback-header">Out-Play · Parent Feedback</p>
      </header>

      <main className="flex-1 px-5 py-8 flex flex-col items-center justify-center max-w-md w-full mx-auto">
        <div className="text-center mb-5 sm:mb-8">
          <p className="text-xs uppercase tracking-widest text-slate-400 mb-1" data-testid="feedback-academy-name">
            {meta.academy_name}
          </p>
          <h1 className="text-2xl sm:text-3xl font-display font-bold leading-tight">
            How was{' '}
            <span className="text-emerald-700" data-testid="feedback-student-name">{meta.student_name}</span>
            &apos;s session?
          </h1>
          <p className="text-sm text-slate-600 mt-2">
            Tap a star — your honest feedback helps us coach better.
          </p>
        </div>

        <StarRow
          rating={rating}
          hover={hover}
          onHover={(n) => !submitting && setHover(n)}
          onLeave={() => !submitting && setHover(0)}
          onTap={handleStarTap}
          disabled={submitting}
        />

        {/* Submitting indicator for high-rating auto-submit */}
        {submitting && !showLowRatingForm && (
          <div className="mt-6 flex items-center gap-2 text-slate-500 text-sm" data-testid="feedback-submitting-state">
            <Loader2 className="h-4 w-4 animate-spin" /> Logging your rating…
          </div>
        )}

        {/* Low-rating expanded form (1-3★) */}
        {showLowRatingForm && !result && (
          <form
            onSubmit={handleLowRatingSubmit}
            className="mt-6 w-full rounded-2xl bg-rose-50 border border-rose-200 p-4"
            data-testid="feedback-low-rating-form"
          >
            <p className="text-sm font-semibold text-rose-900 mb-1">We&apos;re sorry to hear that.</p>
            <p className="text-xs text-rose-700 mb-3">Please tell our management team how we can improve — this goes directly to the academy owner.</p>
            <Textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="What went wrong? Coach behaviour, facility, timing…"
              rows={4}
              maxLength={2000}
              className="bg-white border-rose-300 focus-visible:ring-rose-400"
              data-testid="feedback-low-rating-textarea"
              disabled={submitting}
            />
            <div className="flex items-center justify-between mt-3">
              <button
                type="button"
                className="text-xs text-slate-500 underline"
                onClick={() => { setShowLowRatingForm(false); setRating(0); setComments(''); }}
                disabled={submitting}
                data-testid="feedback-low-rating-cancel-btn"
              >
                Cancel
              </button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="feedback-low-rating-submit-btn"
              >
                {submitting ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Submitting…</> : 'Send privately'}
              </Button>
            </div>
          </form>
        )}

        <p className="text-[11px] text-slate-400 mt-10 text-center max-w-xs">
          1-3★ feedback stays private with the academy owner. 4-5★ may invite you to share publicly on Google.
        </p>
      </main>
    </div>
  );
}
